import React, { useState, useEffect } from 'react';
import { X, Crown, Check, Star, Smartphone, Globe } from 'lucide-react';
import { Modal } from '../Modal';
import { useSubscription } from '../../hooks/useSubscription';
import { getAvailableProducts, purchaseProduct, restorePurchases } from '../../lib/revenuecat/ios';
import { hasPremiumAccess } from '../../lib/subscription';
import { supabase } from '../../lib/supabase';
import { 
  isWebViewGold, 
  triggerRevenueCatPurchase, 
  getProductIdForPlan 
} from '../../lib/revenuecat/webviewgold';

interface RevenueCatProduct {
  identifier: string;
  description: string;
  title: string;
  price: number;
  priceString: string;
  currencyCode: string;
  introPrice?: {
    price: number;
    priceString: string;
    period: string;
    cycles: number;
    periodUnit: string;
    periodNumberOfUnits: number;
  };
}

interface RevenueCatSubscriptionModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function RevenueCatSubscriptionModal({ onClose, onSuccess }: RevenueCatSubscriptionModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<RevenueCatProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const { refresh } = useSubscription();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const availableProducts = await getAvailableProducts();
      setProducts(availableProducts);
      
      // Select the monthly product by default
      const monthlyProduct = availableProducts.find(p => p.identifier.includes('monthly'));
      if (monthlyProduct) {
        setSelectedProduct(monthlyProduct.identifier);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      setError(null);

      // Check if we're in WebViewGold - use URL scheme instead of SDK
      if (isWebViewGold()) {
        // Determine plan type from product identifier
        const planId = selectedProduct.includes('yearly') ? 'yearly' : 'monthly';
        const productId = getProductIdForPlan(planId);
        
        // Trigger RevenueCat purchase via WebViewGold URL scheme
        await triggerRevenueCatPurchase(productId);
        
        // Note: The webhook will handle the subscription update
        // We'll show a message that the purchase is being processed
        setError(null);
        // Don't close immediately - let user know purchase was initiated
        setTimeout(async () => {
          await refresh();
          const { data: { user } } = await supabase.auth.getUser();
          const nowHasPremium = user ? await hasPremiumAccess(user.id) : false;
          if (nowHasPremium) {
            onSuccess();
            onClose();
          } else {
            setError('Purchase initiated. Your subscription will be activated shortly. Please wait a moment and refresh.');
          }
        }, 2000);
      } else {
        // Use SDK approach for native apps (if SDK is available)
        const success = await purchaseProduct(selectedProduct);
        if (success) {
          await refresh();
          onSuccess();
          onClose();
        } else {
          setError('Purchase failed. Please try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Purchase failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      setIsRestoring(true);
      setError(null);

      const success = await restorePurchases();
      if (success) {
        await refresh();
        const { data: { user } } = await supabase.auth.getUser();
        const nowHasPremium = user ? await hasPremiumAccess(user.id) : false;
        if (nowHasPremium) {
          onSuccess();
          onClose();
        } else {
          setError('No previous purchases found to restore.');
        }
      } else {
        setError('Failed to restore purchases. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to restore purchases. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const features = [
    {
      icon: Crown,
      title: 'Create Listings',
      description: 'List your services, events, and venues'
    },
    {
      icon: Star,
      title: 'Build Communities',
      description: 'Create and manage your own wellness communities'
    },
    {
      icon: Globe,
      title: 'Live Stream Access',
      description: 'Join daily wellness sessions and expert talks'
    },
    {
      icon: Check,
      title: 'Priority Support',
      description: 'Get priority customer support and assistance'
    }
  ];

  const formatPrice = (product: RevenueCatProduct) => {
    if (product.introPrice) {
      return `${product.introPrice.priceString} for ${product.introPrice.periodNumberOfUnits} ${product.introPrice.periodUnit.toLowerCase()}(s), then ${product.priceString}/${product.identifier.includes('yearly') ? 'year' : 'month'}`;
    }
    return `${product.priceString}/${product.identifier.includes('yearly') ? 'year' : 'month'}`;
  };

  const getSavings = (product: RevenueCatProduct) => {
    if (product.identifier.includes('yearly')) {
      const monthlyProduct = products.find(p => p.identifier.includes('monthly'));
      if (monthlyProduct) {
        const yearlyEquivalent = monthlyProduct.price * 12;
        const savings = ((yearlyEquivalent - product.price) / yearlyEquivalent * 100).toFixed(0);
        return `Save ${savings}%`;
      }
    }
    return null;
  };

  return (
    <Modal title="Upgrade to Premium" onClose={onClose} fullScreenOnMobile priorityZIndex>
      <div className="max-w-2xl mx-auto">
        {/* Features - branding */}
        <div className="mb-8">
          <h3 className="text-lg font-gelica font-semibold text-[#304F3E] mb-4">Premium Features</h3>
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-accent-text/10 rounded-lg flex items-center justify-center">
                    <Icon className="h-5 w-5 text-accent-text" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#304F3E]">{feature.title}</h4>
                    <p className="text-sm text-content/60">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Products */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text mx-auto"></div>
            <p className="mt-2 text-content/60">Loading products...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-gelica font-semibold text-[#304F3E] mb-4">Choose Your Plan</h3>
            {products.map((product) => {
              const savings = getSavings(product);
              const isYearly = product.identifier.includes('yearly');
              
              return (
                <button
                  key={product.identifier}
                  type="button"
                  onClick={() => setSelectedProduct(product.identifier)}
                  className={`w-full text-left relative border rounded-xl p-4 transition-all ${
                    selectedProduct === product.identifier
                      ? 'border-accent-text bg-accent-text/5 ring-2 ring-accent-text/30'
                      : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedProduct === product.identifier ? 'border-accent-text bg-accent-text' : 'border-gray-300'
                      }`}>
                        {selectedProduct === product.identifier && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-[#304F3E]">{product.title}</h4>
                        <p className="text-sm text-content/60">{product.description}</p>
                        <p className="text-sm font-medium text-accent-text">{formatPrice(product)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {savings && (
                        <span className="bg-accent-text/10 text-accent-text text-xs font-medium px-2 py-1 rounded-full">
                          {savings}
                        </span>
                      )}
                      {isYearly && (
                        <span className="bg-accent-text/15 text-[#304F3E] text-xs font-medium px-2 py-1 rounded-full">
                          Best Value
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Action Buttons - touch-friendly min 48px height */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleRestore}
            disabled={isRestoring}
            className="flex-1 min-h-[48px] px-4 py-3 text-sm font-medium text-[#304F3E] bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isRestoring ? 'Restoring...' : 'Restore Purchases'}
          </button>
          
          <button
            type="button"
            onClick={handlePurchase}
            disabled={loading || !selectedProduct}
            className="flex-1 min-h-[48px] px-4 py-3 text-sm font-medium text-white bg-accent-text rounded-xl hover:bg-accent-text/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Processing...' : 'Subscribe Now'}
          </button>
        </div>

        {/* Terms */}
        <div className="mt-6 text-xs text-content/50 text-center">
          <p>
            Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period.
            Your account will be charged for renewal within 24 hours prior to the end of the current period.
            You can manage and cancel your subscriptions by going to your account settings on the App Store after purchase.
          </p>
        </div>
      </div>
    </Modal>
  );
}


